import { useMemo, useState } from "react";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import type { EnduserObservation, ObservationComponent } from "@tellescope/types-models";
import { useEnduserObservations } from "../../../hooks/useEnduserObservations";
import { useEnduserCurrentEnduser, useEnduserStore, useEnduserUpdateCurrentEnduser } from "../../../store/hooks";
import { useLogNutritionEntry, type LogNutritionEntryParams } from "../../../hooks/useLogNutritionEntry";
import { CALORIE_UNIT, MACRO_UNIT, WATER_UNIT, WEIGHT_UNIT } from "../../../utils/nutritionUnitConversions";
import { isSameDay, sumMacro, type MetricId } from "./metrics";

export interface UpdateNutritionEntryParams {
  id: string;
  measurement?: { value: number; unit: string };
  components?: ObservationComponent[];
}

export interface NutritionTargets {
  dailyCalorieTarget?: number;
  dailyProteinTarget?: number;
  dailyCarbTarget?: number;
  dailyFatTarget?: number;
  dailyFiberTarget?: number;
  dailyWaterTarget?: number;
}

export type NutritionEntryType = "weight" | "food" | "water";

export type WeightMacroTrackerView =
  | { name: "dashboard" }
  | { name: "history" }
  | { name: "metric-detail"; metricId: MetricId }
  | { name: "log-entry"; editEntryId?: string; returnTo: WeightMacroTrackerView };

// Re-exported for backward compatibility — these now live in metrics.ts (the data layer shared
// with the metric-detail/dashboard views), but existing test imports reference this module.
export { isSameDay, sumMacro };

/**
 * This feature is enduser-driven — patients shouldn't see or manage entries logged on their
 * behalf by staff (e.g. a nurse recording a vital during a call). The backend has no server-side
 * filter for this on read (enduser_observations has no creator param on getSome/getMany/load), so
 * this filters client-side after fetching, using the same `creator === session.id` check the
 * server itself performs for the creatorOnly update/delete gate — every observation this enduser
 * ever creates gets `creator` set to their own id unconditionally at write time, so this is exact,
 * not a guess. Entries this filters out are never shown, so update/delete are safe to call
 * unconditionally on anything this hook exposes.
 */
export const isOwnNutritionEntry = (
  observation: EnduserObservation,
  type: NutritionEntryType,
  currentEnduserId: string | undefined
): observation is EnduserObservation & { type: NutritionEntryType } =>
  observation.type === type && !!currentEnduserId && observation.creator === currentEnduserId;

export interface TodayTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  water: number;
}

/** Aggregates a day's logged food and water observations into totals per macro/water. */
export const computeTodayTotals = (
  todaysFood: EnduserObservation[],
  todaysWater: EnduserObservation[]
): TodayTotals => ({
  calories: sumMacro(todaysFood),
  protein: sumMacro(todaysFood, "Protein"),
  carbs: sumMacro(todaysFood, "Carbohydrate"),
  fat: sumMacro(todaysFood, "Fat"),
  fiber: sumMacro(todaysFood, "Fiber"),
  water: sumMacro(todaysWater),
});

export const useWeightMacroTrackerBlock = () => {
  const { observations, isLoading, error } = useEnduserObservations();
  const currentEnduser = useEnduserCurrentEnduser();
  const session = useEnduserStore((state) => state.session);
  const updateCurrentEnduser = useEnduserUpdateCurrentEnduser();

  const { logEntryAsync, isPending: isLogging } = useLogNutritionEntry();

  // Updates a self-created weight/macro/water entry. The backend enforces creatorOnly — this
  // will fail if the entry was created by staff on the patient's behalf rather than by the
  // patient themselves. Callers should only surface an edit affordance for entries they know
  // the current session created, and handle a rejected mutation as "this entry can't be edited"
  // rather than a generic error.
  const updateMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "updateOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });
  const isUpdating = updateMutation.isPending;
  const updateEntryAsync = ({ id, measurement, components }: UpdateNutritionEntryParams) =>
    updateMutation.mutateAsync({ id, ...(measurement && { measurement }), ...(components && { components }) });

  // Deletes a self-created weight/macro/water entry. Same creatorOnly caveat as updateEntryAsync above.
  const deleteMutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "deleteOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });
  const isDeleting = deleteMutation.isPending;
  const deleteEntryAsync = (id: string) => deleteMutation.mutateAsync(id);

  // Reads/writes the patient's own daily nutrition targets, stored as built-in fields on Enduser.
  const targetsMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });
  const isSavingTargets = targetsMutation.isPending;
  const setTargetsAsync = async (newTargets: NutritionTargets) => {
    const result = await targetsMutation.mutateAsync({ id: currentEnduser?.id ?? "", ...newTargets });
    updateCurrentEnduser(newTargets);
    return result;
  };

  const [view, setView] = useState<WeightMacroTrackerView>({ name: "dashboard" });
  const goToDashboard = () => setView({ name: "dashboard" });
  const goToHistory = () => setView({ name: "history" });
  const goToMetricDetail = (metricId: MetricId) => setView({ name: "metric-detail", metricId });
  const goToLogEntry = (editEntryId?: string) =>
    setView((current) => ({ name: "log-entry", editEntryId, returnTo: current }));
  const goBackFromLogEntry = () => {
    if (view.name === "log-entry") setView(view.returnTo);
  };

  const weightEntries = useMemo(
    () => observations.filter((o) => isOwnNutritionEntry(o, "weight", currentEnduser?.id)),
    [observations, currentEnduser?.id]
  );
  const foodEntries = useMemo(
    () => observations.filter((o) => isOwnNutritionEntry(o, "food", currentEnduser?.id)),
    [observations, currentEnduser?.id]
  );
  const waterEntries = useMemo(
    () => observations.filter((o) => isOwnNutritionEntry(o, "water", currentEnduser?.id)),
    [observations, currentEnduser?.id]
  );

  const latestWeight = weightEntries[0];

  const today = useMemo(() => new Date(), []);
  const todaysFood = useMemo(
    () => foodEntries.filter((o) => isSameDay(new Date(o.timestamp), today)),
    [foodEntries, today]
  );
  const todaysWater = useMemo(
    () => waterEntries.filter((o) => isSameDay(new Date(o.timestamp), today)),
    [waterEntries, today]
  );

  const todayTotals = useMemo<TodayTotals>(
    () => computeTodayTotals(todaysFood, todaysWater),
    [todaysFood, todaysWater]
  );

  const targets: NutritionTargets = {
    dailyCalorieTarget: currentEnduser?.dailyCalorieTarget,
    dailyProteinTarget: currentEnduser?.dailyProteinTarget,
    dailyCarbTarget: currentEnduser?.dailyCarbTarget,
    dailyFatTarget: currentEnduser?.dailyFatTarget,
    dailyFiberTarget: currentEnduser?.dailyFiberTarget,
    dailyWaterTarget: currentEnduser?.dailyWaterTarget,
  };

  const logWeight = async (valueInCanonicalUnit: number): Promise<void> => {
    await logEntryAsync({ type: "weight", measurement: { value: valueInCanonicalUnit, unit: WEIGHT_UNIT } });
  };

  const logWater = async (valueInCanonicalUnit: number): Promise<void> => {
    await logEntryAsync({ type: "water", measurement: { value: valueInCanonicalUnit, unit: WATER_UNIT } });
  };

  const logFood = async (params: {
    totalCalories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    items?: LogNutritionEntryParams["components"];
  }): Promise<void> => {
    const macroComponents: NonNullable<LogNutritionEntryParams["components"]> = [
      { code: { text: "Protein" }, valueQuantity: { value: params.protein, unit: MACRO_UNIT } },
      { code: { text: "Carbohydrate" }, valueQuantity: { value: params.carbs, unit: MACRO_UNIT } },
      { code: { text: "Fat" }, valueQuantity: { value: params.fat, unit: MACRO_UNIT } },
      { code: { text: "Fiber" }, valueQuantity: { value: params.fiber, unit: MACRO_UNIT } },
      ...(params.items ?? []),
    ];
    await logEntryAsync({
      type: "food",
      measurement: { value: params.totalCalories, unit: CALORIE_UNIT },
      components: macroComponents,
    });
  };

  // Safe to call unconditionally on anything exposed by this hook — weightEntries/foodEntries/
  // waterEntries are already filtered to entries this enduser created, matching the server's own
  // creatorOnly gate exactly, so there's no staff-created entry these could ever be called on.
  const updateEntry = (params: UpdateNutritionEntryParams) => updateEntryAsync(params);
  const deleteEntry = (id: string) => deleteEntryAsync(id);

  return {
    isLoading,
    error,
    latestWeight,
    weightEntries,
    foodEntries,
    waterEntries,
    todayTotals,
    targets,
    logWeight,
    logWater,
    logFood,
    updateEntry,
    deleteEntry,
    setTargetsAsync,
    isLogging,
    isUpdating,
    isDeleting,
    isSavingTargets,
    view,
    goToDashboard,
    goToHistory,
    goToMetricDetail,
    goToLogEntry,
    goBackFromLogEntry,
  };
};
